package com.mgacreative.hotel.shared.ui.designsystem.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.grid.LazyGridState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch

/**
 * Modifier to render a sleek, fully interactive vertical scrollbar on any container with [ScrollState].
 * Supports click-to-jump and drag-to-scroll.
 */
fun Modifier.verticalScrollbar(
    scrollState: ScrollState,
    width: Dp = 6.dp,
    thumbColor: Color = Color(0xFF94A3B8),
    trackColor: Color = Color(0x10000000),
    cornerRadius: Dp = 3.dp,
    paddingEnd: Dp = 3.dp
): Modifier = composed {
    val coroutineScope = rememberCoroutineScope()
    val density = LocalDensity.current
    val canScroll = scrollState.maxValue > 0
    var isDragging by remember { mutableStateOf(false) }

    val alpha by animateFloatAsState(
        targetValue = if (canScroll) (if (isDragging) 1f else 0.85f) else 0f,
        animationSpec = tween(durationMillis = 200),
        label = "scrollbarAlpha"
    )

    this
        .pointerInput(scrollState, canScroll) {
            if (!canScroll) return@pointerInput
            val hitAreaWidthPx = with(density) { (width + paddingEnd + 12.dp).toPx() }

            detectTapGestures { offset ->
                if (offset.x >= size.width - hitAreaWidthPx) {
                    val viewportHeight = size.height.toFloat()
                    val totalContentHeight = viewportHeight + scrollState.maxValue
                    val thumbHeight = ((viewportHeight / totalContentHeight) * viewportHeight)
                        .coerceIn(with(density) { 36.dp.toPx() }, viewportHeight)
                    val scrollableTrack = (viewportHeight - thumbHeight).coerceAtLeast(1f)
                    val targetProgress = ((offset.y - thumbHeight / 2f) / scrollableTrack).coerceIn(0f, 1f)
                    val targetScroll = (targetProgress * scrollState.maxValue).toInt()
                    coroutineScope.launch {
                        scrollState.animateScrollTo(targetScroll)
                    }
                }
            }
        }
        .pointerInput(scrollState, canScroll) {
            if (!canScroll) return@pointerInput
            val hitAreaWidthPx = with(density) { (width + paddingEnd + 12.dp).toPx() }

            detectDragGestures(
                onDragStart = { offset ->
                    if (offset.x >= size.width - hitAreaWidthPx) {
                        isDragging = true
                    }
                },
                onDragEnd = { isDragging = false },
                onDragCancel = { isDragging = false },
                onDrag = { change, dragAmount ->
                    if (isDragging) {
                        change.consume()
                        val viewportHeight = size.height.toFloat()
                        val totalContentHeight = viewportHeight + scrollState.maxValue
                        val thumbHeight = ((viewportHeight / totalContentHeight) * viewportHeight)
                            .coerceIn(with(density) { 36.dp.toPx() }, viewportHeight)
                        val scrollableTrack = (viewportHeight - thumbHeight).coerceAtLeast(1f)
                        val scrollDelta = (dragAmount.y / scrollableTrack) * scrollState.maxValue
                        coroutineScope.launch {
                            scrollState.scrollBy(scrollDelta)
                        }
                    }
                }
            )
        }
        .drawWithContent {
            drawContent()

            if (canScroll && alpha > 0f) {
                val totalContentHeight = size.height + scrollState.maxValue
                val viewportHeight = size.height
                if (totalContentHeight <= 0f || viewportHeight <= 0f) return@drawWithContent

                val scrollbarHeight = ((viewportHeight / totalContentHeight) * viewportHeight)
                    .coerceIn(36.dp.toPx(), viewportHeight)
                val scrollProgress = (scrollState.value.toFloat() / scrollState.maxValue.toFloat()).coerceIn(0f, 1f)
                val scrollbarTop = scrollProgress * (viewportHeight - scrollbarHeight)
                val scrollbarWidthPx = (if (isDragging) width + 2.dp else width).toPx()
                val paddingEndPx = paddingEnd.toPx()
                val radiusPx = cornerRadius.toPx()

                // Track
                drawRoundRect(
                    color = trackColor.copy(alpha = trackColor.alpha * alpha),
                    topLeft = Offset(size.width - scrollbarWidthPx - paddingEndPx, 0f),
                    size = Size(scrollbarWidthPx, viewportHeight),
                    cornerRadius = CornerRadius(radiusPx, radiusPx)
                )

                // Thumb
                val activeThumbColor = if (isDragging) thumbColor.copy(alpha = 1f) else thumbColor.copy(alpha = thumbColor.alpha * alpha)
                drawRoundRect(
                    color = activeThumbColor,
                    topLeft = Offset(size.width - scrollbarWidthPx - paddingEndPx, scrollbarTop),
                    size = Size(scrollbarWidthPx, scrollbarHeight),
                    cornerRadius = CornerRadius(radiusPx, radiusPx)
                )
            }
        }
}

/**
 * Modifier to render a sleek, fully interactive vertical scrollbar on any [LazyListState] (LazyColumn).
 * Supports click-to-jump and drag-to-scroll.
 */
fun Modifier.verticalScrollbar(
    lazyListState: LazyListState,
    width: Dp = 6.dp,
    thumbColor: Color = Color(0xFF94A3B8),
    trackColor: Color = Color(0x10000000),
    cornerRadius: Dp = 3.dp,
    paddingEnd: Dp = 3.dp
): Modifier = composed {
    val coroutineScope = rememberCoroutineScope()
    val density = LocalDensity.current
    val layoutInfo = lazyListState.layoutInfo
    val totalItems = layoutInfo.totalItemsCount
    val visibleItems = layoutInfo.visibleItemsInfo.size
    val canScroll = totalItems > visibleItems && totalItems > 0
    var isDragging by remember { mutableStateOf(false) }

    val alpha by animateFloatAsState(
        targetValue = if (canScroll) (if (isDragging) 1f else 0.85f) else 0f,
        animationSpec = tween(durationMillis = 200),
        label = "lazyScrollbarAlpha"
    )

    this
        .pointerInput(lazyListState, canScroll, totalItems) {
            if (!canScroll) return@pointerInput
            val hitAreaWidthPx = with(density) { (width + paddingEnd + 12.dp).toPx() }

            detectTapGestures { offset ->
                if (offset.x >= size.width - hitAreaWidthPx) {
                    val viewportHeight = size.height.toFloat()
                    val estimatedVisibleFraction = (visibleItems.toFloat() / totalItems.toFloat()).coerceIn(0.08f, 1f)
                    val thumbHeight = (viewportHeight * estimatedVisibleFraction).coerceIn(with(density) { 36.dp.toPx() }, viewportHeight)
                    val scrollableTrack = (viewportHeight - thumbHeight).coerceAtLeast(1f)
                    val targetProgress = ((offset.y - thumbHeight / 2f) / scrollableTrack).coerceIn(0f, 1f)
                    val targetIndex = (targetProgress * (totalItems - 1)).toInt().coerceIn(0, totalItems - 1)
                    coroutineScope.launch {
                        lazyListState.animateScrollToItem(targetIndex)
                    }
                }
            }
        }
        .pointerInput(lazyListState, canScroll, totalItems) {
            if (!canScroll) return@pointerInput
            val hitAreaWidthPx = with(density) { (width + paddingEnd + 12.dp).toPx() }

            detectDragGestures(
                onDragStart = { offset ->
                    if (offset.x >= size.width - hitAreaWidthPx) {
                        isDragging = true
                    }
                },
                onDragEnd = { isDragging = false },
                onDragCancel = { isDragging = false },
                onDrag = { change, dragAmount ->
                    if (isDragging) {
                        change.consume()
                        val viewportHeight = size.height.toFloat()
                        val estimatedVisibleFraction = (visibleItems.toFloat() / totalItems.toFloat()).coerceIn(0.08f, 1f)
                        val thumbHeight = (viewportHeight * estimatedVisibleFraction).coerceIn(with(density) { 36.dp.toPx() }, viewportHeight)
                        val scrollableTrack = (viewportHeight - thumbHeight).coerceAtLeast(1f)
                        val avgItemHeight = if (visibleItems > 0) viewportHeight / visibleItems else 60f
                        val scrollDelta = (dragAmount.y / scrollableTrack) * (totalItems * avgItemHeight)
                        coroutineScope.launch {
                            lazyListState.scrollBy(scrollDelta)
                        }
                    }
                }
            )
        }
        .drawWithContent {
            drawContent()

            if (canScroll && alpha > 0f) {
                val viewportHeight = size.height
                if (viewportHeight <= 0f) return@drawWithContent

                val firstVisibleIndex = lazyListState.firstVisibleItemIndex
                val estimatedTotalSize = totalItems.toFloat()
                val estimatedVisibleFraction = (visibleItems.toFloat() / estimatedTotalSize).coerceIn(0.08f, 1f)
                val scrollbarHeight = (viewportHeight * estimatedVisibleFraction).coerceIn(36.dp.toPx(), viewportHeight)

                val maxIndex = (totalItems - visibleItems).coerceAtLeast(1).toFloat()
                val scrollProgress = (firstVisibleIndex.toFloat() / maxIndex).coerceIn(0f, 1f)
                val scrollbarTop = scrollProgress * (viewportHeight - scrollbarHeight)
                val scrollbarWidthPx = (if (isDragging) width + 2.dp else width).toPx()
                val paddingEndPx = paddingEnd.toPx()
                val radiusPx = cornerRadius.toPx()

                // Track
                drawRoundRect(
                    color = trackColor.copy(alpha = trackColor.alpha * alpha),
                    topLeft = Offset(size.width - scrollbarWidthPx - paddingEndPx, 0f),
                    size = Size(scrollbarWidthPx, viewportHeight),
                    cornerRadius = CornerRadius(radiusPx, radiusPx)
                )

                // Thumb
                val activeThumbColor = if (isDragging) thumbColor.copy(alpha = 1f) else thumbColor.copy(alpha = thumbColor.alpha * alpha)
                drawRoundRect(
                    color = activeThumbColor,
                    topLeft = Offset(size.width - scrollbarWidthPx - paddingEndPx, scrollbarTop),
                    size = Size(scrollbarWidthPx, scrollbarHeight),
                    cornerRadius = CornerRadius(radiusPx, radiusPx)
                )
            }
        }
}

/**
 * Modifier to render a sleek, fully interactive vertical scrollbar on any [LazyGridState] (LazyVerticalGrid).
 * Supports click-to-jump and drag-to-scroll.
 */
fun Modifier.verticalScrollbar(
    lazyGridState: LazyGridState,
    width: Dp = 6.dp,
    thumbColor: Color = Color(0xFF94A3B8),
    trackColor: Color = Color(0x10000000),
    cornerRadius: Dp = 3.dp,
    paddingEnd: Dp = 3.dp
): Modifier = composed {
    val coroutineScope = rememberCoroutineScope()
    val density = LocalDensity.current
    val layoutInfo = lazyGridState.layoutInfo
    val totalItems = layoutInfo.totalItemsCount
    val visibleItems = layoutInfo.visibleItemsInfo.size
    val canScroll = totalItems > visibleItems && totalItems > 0
    var isDragging by remember { mutableStateOf(false) }

    val alpha by animateFloatAsState(
        targetValue = if (canScroll) (if (isDragging) 1f else 0.85f) else 0f,
        animationSpec = tween(durationMillis = 200),
        label = "gridScrollbarAlpha"
    )

    this
        .pointerInput(lazyGridState, canScroll, totalItems) {
            if (!canScroll) return@pointerInput
            val hitAreaWidthPx = with(density) { (width + paddingEnd + 12.dp).toPx() }

            detectTapGestures { offset ->
                if (offset.x >= size.width - hitAreaWidthPx) {
                    val viewportHeight = size.height.toFloat()
                    val estimatedVisibleFraction = (visibleItems.toFloat() / totalItems.toFloat()).coerceIn(0.08f, 1f)
                    val thumbHeight = (viewportHeight * estimatedVisibleFraction).coerceIn(with(density) { 36.dp.toPx() }, viewportHeight)
                    val scrollableTrack = (viewportHeight - thumbHeight).coerceAtLeast(1f)
                    val targetProgress = ((offset.y - thumbHeight / 2f) / scrollableTrack).coerceIn(0f, 1f)
                    val targetIndex = (targetProgress * (totalItems - 1)).toInt().coerceIn(0, totalItems - 1)
                    coroutineScope.launch {
                        lazyGridState.animateScrollToItem(targetIndex)
                    }
                }
            }
        }
        .pointerInput(lazyGridState, canScroll, totalItems) {
            if (!canScroll) return@pointerInput
            val hitAreaWidthPx = with(density) { (width + paddingEnd + 12.dp).toPx() }

            detectDragGestures(
                onDragStart = { offset ->
                    if (offset.x >= size.width - hitAreaWidthPx) {
                        isDragging = true
                    }
                },
                onDragEnd = { isDragging = false },
                onDragCancel = { isDragging = false },
                onDrag = { change, dragAmount ->
                    if (isDragging) {
                        change.consume()
                        val viewportHeight = size.height.toFloat()
                        val estimatedVisibleFraction = (visibleItems.toFloat() / totalItems.toFloat()).coerceIn(0.08f, 1f)
                        val thumbHeight = (viewportHeight * estimatedVisibleFraction).coerceIn(with(density) { 36.dp.toPx() }, viewportHeight)
                        val scrollableTrack = (viewportHeight - thumbHeight).coerceAtLeast(1f)
                        val avgItemHeight = if (visibleItems > 0) viewportHeight / visibleItems else 60f
                        val scrollDelta = (dragAmount.y / scrollableTrack) * (totalItems * avgItemHeight)
                        coroutineScope.launch {
                            lazyGridState.scrollBy(scrollDelta)
                        }
                    }
                }
            )
        }
        .drawWithContent {
            drawContent()

            if (canScroll && alpha > 0f) {
                val viewportHeight = size.height
                if (viewportHeight <= 0f) return@drawWithContent

                val firstVisibleIndex = lazyGridState.firstVisibleItemIndex
                val estimatedTotalSize = totalItems.toFloat()
                val estimatedVisibleFraction = (visibleItems.toFloat() / estimatedTotalSize).coerceIn(0.08f, 1f)
                val scrollbarHeight = (viewportHeight * estimatedVisibleFraction).coerceIn(36.dp.toPx(), viewportHeight)

                val maxIndex = (totalItems - visibleItems).coerceAtLeast(1).toFloat()
                val scrollProgress = (firstVisibleIndex.toFloat() / maxIndex).coerceIn(0f, 1f)
                val scrollbarTop = scrollProgress * (viewportHeight - scrollbarHeight)
                val scrollbarWidthPx = (if (isDragging) width + 2.dp else width).toPx()
                val paddingEndPx = paddingEnd.toPx()
                val radiusPx = cornerRadius.toPx()

                // Track
                drawRoundRect(
                    color = trackColor.copy(alpha = trackColor.alpha * alpha),
                    topLeft = Offset(size.width - scrollbarWidthPx - paddingEndPx, 0f),
                    size = Size(scrollbarWidthPx, viewportHeight),
                    cornerRadius = CornerRadius(radiusPx, radiusPx)
                )

                // Thumb
                val activeThumbColor = if (isDragging) thumbColor.copy(alpha = 1f) else thumbColor.copy(alpha = thumbColor.alpha * alpha)
                drawRoundRect(
                    color = activeThumbColor,
                    topLeft = Offset(size.width - scrollbarWidthPx - paddingEndPx, scrollbarTop),
                    size = Size(scrollbarWidthPx, scrollbarHeight),
                    cornerRadius = CornerRadius(radiusPx, radiusPx)
                )
            }
        }
}

/**
 * Modern Scrollable Column with integrated right-edge vertical scrollbar.
 */
@Composable
fun HFScrollableColumn(
    modifier: Modifier = Modifier,
    scrollState: ScrollState = rememberScrollState(),
    verticalArrangement: Arrangement.Vertical = Arrangement.Top,
    horizontalAlignment: Alignment.Horizontal = Alignment.Start,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(
        modifier = modifier
            .verticalScroll(scrollState)
            .verticalScrollbar(scrollState),
        verticalArrangement = verticalArrangement,
        horizontalAlignment = horizontalAlignment,
        content = content
    )
}

